import { PlusIcon } from 'lucide-react';
import { type ComponentProps, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../common/functions/cn';
import { fuzzyMatch } from '../../../common/functions/fuzzy-match';
import { Skeleton } from '../../skeleton/skeleton.component';
import { InputGroupAddon } from '../input-group/input-group';
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValueChip,
  useComboboxFilteredItems,
} from './combobox';

export interface CreatableMarker {
  __create: string;
}

export const isCreatable = (item: unknown): item is CreatableMarker => {
  return typeof item === 'object' && item !== null && '__create' in item;
};

export interface QueryProps {
  /**
   * Opts into server-side filtering when supplied with `query` and `onQueryChange`: the preset forwards typing to the caller and trusts `items` as the rendered set.
   * Whether `items` clears between queries is up to the caller: clearing shows the loading state in the empty slot, keeping stale items keeps them visible until the new page arrives.
   * The preset owns what the input displays and does not read this back, so it is the caller's mirror of the last reported query, pass the state you store in `onQueryChange`.
   */
  query?: string;
  /** Receives the typed query. Server-side filtering only kicks in when this is supplied together with `query`; either one alone leaves the preset on local filtering. */
  onQueryChange?: (query: string) => void;
}

/**
 * The base-ui change reasons that mean the user produced the text. Every other reason is base-ui rewriting it.
 * `input-clear` covers deleting the last character: base-ui fires a deselect (`onValueChange(null)`), then may
 * repopulate the input from the *controlled* selection (when the caller ignores the deselect). Treat it as user input
 * so the input stays empty.
 */
const USER_EDIT_REASONS: ReadonlySet<string> = new Set(['input-change', 'input-clear']);

/**
 * Owns the text the user has typed into a combobox, so the presets can keep `inputValue` controlled for their whole lifetime.
 * Base UI latches whether `inputValue` is controlled on the first render, so a preset whose caller adds or drops `query`/`onQueryChange` mid-life must not switch between a string and `undefined`.
 * `typedQuery` is `null` while the user isn't editing, which is the presets' cue to display the selection instead.
 * Base UI would normally do that sync itself, but controlling `inputValue` turns it off.
 */
export const useComboboxInputValue = (onQueryChange: ((query: string) => void) | undefined) => {
  const [typedQuery, setTypedQuery] = useState<string | null>(null);
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;

  const handleInputValueChange = useCallback((next: string, reason: string) => {
    const nextQuery = USER_EDIT_REASONS.has(reason) ? next : null;
    setTypedQuery(nextQuery);
    onQueryChangeRef.current?.(nextQuery ?? '');
  }, []);

  const clearQuery = useCallback(() => {
    setTypedQuery(null);
    onQueryChangeRef.current?.('');
  }, []);

  return { typedQuery, handleInputValueChange, clearQuery };
};

export interface CreatableProps<T> {
  /** When supplied, a synthetic "Create" option is rendered for a non-matching query. Selecting it calls `onCreate(query)` and commits the returned value via `onValueChange`. */
  onCreate?: (query: string) => Promise<T> | T;
  /** Optional renderer for the synthetic create option. Receives the current query. Defaults to the `comboboxCreate` text. Inert without `onCreate`. */
  renderCreateOption?: (query: string) => ReactNode;
}

/** How far before the tail the load-more probe fires. Matches the popup's small scroll area. */
const TAIL_MARGIN = '120px';

export const useCreatableHelpers = <T,>(
  items: readonly T[],
  getLabel: (item: T) => string,
  itemToStringLabel: ((item: T) => string) | undefined,
  onCreate: ((query: string) => Promise<T> | T) | undefined,
  effectiveQuery: string
) => {
  const getLabelRef = useRef(getLabel);
  getLabelRef.current = getLabel;
  const itemToStringLabelRef = useRef(itemToStringLabel);
  itemToStringLabelRef.current = itemToStringLabel;

  const itemsForView = useMemo(() => {
    if (!onCreate) {
      return items;
    }

    const trimmed = effectiveQuery.trim();
    if (!trimmed) {
      return items;
    }

    const lowered = trimmed.toLowerCase();
    const exactMatch = items.some((item) => getLabelRef.current(item).trim().toLowerCase() === lowered);
    if (exactMatch) {
      return items;
    }

    return [...items, { __create: trimmed } as unknown as T];
  }, [items, onCreate, effectiveQuery]);

  const localFilter = useCallback(
    (item: T, q: string) => (isCreatable(item) ? true : fuzzyMatch(getLabelRef.current(item), q)),
    []
  );

  const stableItemToStringLabel = useCallback((item: T | null) => {
    if (!item) {
      return '';
    }
    if (isCreatable(item)) {
      return item.__create;
    }
    return itemToStringLabelRef.current ? itemToStringLabelRef.current(item) : getLabelRef.current(item);
  }, []);

  return { itemsForView, localFilter, stableItemToStringLabel };
};

/**
 * Key-based item equality that also handles the synthetic creatable marker, so cloned values (e.g. round-tripped
 * through react-hook-form) still match their item. Shared by the single and multi presets.
 */
export const useCreatableItemEquality = <T,>(getKey: (item: T) => string | number) =>
  useCallback(
    (item: T, v: T) => {
      const itemIsCreatable = isCreatable(item);
      const valueIsCreatable = isCreatable(v);
      if (itemIsCreatable || valueIsCreatable) {
        return itemIsCreatable && valueIsCreatable && item.__create === v.__create;
      }
      return getKey(item) === getKey(v);
    },
    [getKey]
  );

interface ComboboxPresetContentProps<T> extends ComboboxItemListProps<T> {
  /** Anchors the popup to something other than the input, for the chips variant. */
  anchor?: ComponentProps<typeof ComboboxContent>['anchor'];
  emptyMessage?: ReactNode;
  /** True for the initial fetch only, so background refetches and filter-to-empty results keep the real message. */
  showLoadingCopy: boolean;
  /** Extra content below the list, outside the filtered collection (e.g. `ComboboxActions`). */
  children?: ReactNode;
  /** Renders in place under the input instead of in a popup. */
  inline?: boolean;
}

/** The popup half of both presets: the empty/loading slot, the option list, and any caller-supplied extras. */
export const ComboboxPresetContent = <T,>({
  anchor,
  emptyMessage,
  showLoadingCopy,
  children,
  inline = false,
  ...listProps
}: ComboboxPresetContentProps<T>) => {
  const { t } = useTranslation();

  const content = (
    <>
      {/* Base UI only fills the empty slot when nothing matches, so inline it can hide on `:empty` instead of keying
          off the popup's `data-empty`. */}
      <ComboboxEmpty
        className={cn(
          inline && 'flex empty:hidden',
          inline &&
            showLoadingCopy &&
            'h-[min(var(--combobox-list-max-height,18.5rem),calc(var(--available-height,100dvh)-9px))] flex-col items-stretch justify-start overflow-hidden p-1'
        )}
      >
        {inline && showLoadingCopy ? (
          <LoadingRows />
        ) : showLoadingCopy ? (
          t(($) => $.comboboxLoading)
        ) : (
          (emptyMessage ?? t(($) => $.comboboxEmpty))
        )}
      </ComboboxEmpty>
      <ComboboxItemList {...listProps} />
      {children}
    </>
  );

  if (inline) {
    return (
      <div data-slot="combobox-inline-content" className="flex flex-col">
        {content}
      </div>
    );
  }

  return <ComboboxContent anchor={anchor}>{content}</ComboboxContent>;
};

/** Draws an inline combobox's input and list as one field, with the list under a divider. */
export const ComboboxInlineFrame = ({ children }: { children: ReactNode }) => (
  <div
    data-slot="combobox-inline"
    className={cn(
      'flex min-w-0 flex-col overflow-hidden rounded-md border border-input bg-background',
      'has-[[data-slot=input-group-control]:focus-visible]:border-ring',
      'has-[[data-slot=input-group-control]:focus-visible]:ring-2 has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50',
      'has-[[data-slot][aria-invalid=true]]:border-error',
      'has-[[data-slot][aria-invalid=true]]:ring-2 has-[[data-slot][aria-invalid=true]]:ring-error/20',
      'has-[[data-slot][aria-invalid=true]]:has-[[data-slot=input-group-control]:focus-visible]:ring-error/60'
    )}
  >
    {children}
  </div>
);

/** Shows an inline combobox's selection, which a search can filter out of the list, and clears it. */
export const ComboboxSelectionChip = ({
  children,
  title,
  onClear,
}: {
  children: ReactNode;
  title?: string;
  onClear?: () => void;
}) => (
  <InputGroupAddon align="inline-end" className="min-w-0 max-w-1/2">
    <ComboboxValueChip title={title} onClear={onClear}>
      {children}
    </ComboboxValueChip>
  </InputGroupAddon>
);

/** The input group's `has-*` focus styles outrank plain classes, hence the `!`. */
export const comboboxInlineInputStyles = cn(
  'rounded-none border-x-0 border-t-0 bg-transparent',
  'border-border! ring-0!'
);

const LOADING_ROW_WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-2/5', 'w-3/5', 'w-4/5'];
const LOADING_ROWS = Array.from({ length: 36 }, (_, id) => ({
  id,
  width: LOADING_ROW_WIDTHS[id % LOADING_ROW_WIDTHS.length],
}));

/** Fills the list's max height, which a first page of options always reaches, so the list arrives without a jump. */
const LoadingRows = () => {
  const { t } = useTranslation();
  return (
    <>
      <span className="sr-only">{t(($) => $.comboboxLoading)}</span>
      {LOADING_ROWS.map((row) => (
        <span key={row.id} className="flex min-h-8 shrink-0 items-center px-1.5">
          <Skeleton className={cn('h-3.5', row.width)} />
        </span>
      ))}
    </>
  );
};

interface ComboboxItemListProps<T> {
  items: readonly T[];
  getKey: (item: T) => string | number;
  getLabel: (item: T) => string;
  getDescription?: (item: T) => string | undefined;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore?: () => void;
  renderCreateOption?: (query: string) => ReactNode;
  isServerFilter: boolean;
}

/**
 * Renders the popup option list. Every option is a real DOM node with `content-visibility: auto`, so the browser
 * skips layout/paint for off-screen rows without the complexity of a virtualizer. Pagination is driven by an
 * `IntersectionObserver` on a tail probe (`useInfiniteScroll`); it stays inert when `hasMore` is false.
 */
const ComboboxItemList = <T,>(props: ComboboxItemListProps<T>) => {
  return props.isServerFilter ? (
    <ComboboxRows
      renderItems={props.items}
      getKey={props.getKey}
      getLabel={props.getLabel}
      getDescription={props.getDescription}
      isLoading={props.isLoading}
      hasMore={props.hasMore}
      onLoadMore={props.onLoadMore}
      renderCreateOption={props.renderCreateOption}
    />
  ) : (
    <LocallyFilteredRows<T> {...props} />
  );
};

const LocallyFilteredRows = <T,>({
  getKey,
  getLabel,
  getDescription,
  isLoading,
  hasMore,
  onLoadMore,
  renderCreateOption,
}: ComboboxItemListProps<T>) => {
  const renderItems = useComboboxFilteredItems<T>();
  return (
    <ComboboxRows
      renderItems={renderItems}
      getKey={getKey}
      getLabel={getLabel}
      getDescription={getDescription}
      isLoading={isLoading}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      renderCreateOption={renderCreateOption}
    />
  );
};

interface ComboboxRowsProps<T> {
  renderItems: readonly T[];
  getKey: (item: T) => string | number;
  getLabel: (item: T) => string;
  getDescription?: (item: T) => string | undefined;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore?: () => void;
  renderCreateOption?: (query: string) => ReactNode;
}

const ComboboxRows = <T,>({
  renderItems,
  getKey,
  getLabel,
  getDescription,
  isLoading,
  hasMore,
  onLoadMore,
  renderCreateOption,
}: ComboboxRowsProps<T>) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const tailRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const tail = tailRef.current;
    const root = scrollRef.current;
    if (!tail || !root || !hasMore) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.at(-1)?.isIntersecting && !isLoadingRef.current) {
          onLoadMoreRef.current?.();
        }
      },
      { root, rootMargin: TAIL_MARGIN }
    );
    observer.observe(tail);
    return () => observer.disconnect();
  }, [hasMore]);

  // Nothing to show and nothing more to fetch: leave the popup to `ComboboxEmpty`. When more pages remain we still
  // render the list, so the tail probe exists and pagination can resume from an empty page (a source may return a
  // `next` cursor alongside zero rows).
  if (renderItems.length === 0 && !hasMore) {
    return null;
  }

  // `content-visibility: auto` lets the browser skip rendering off-screen rows; `contain-intrinsic-size`
  // reserves each row's box (~32px, ~44px with a description) so the scrollbar stays stable while those rows are unrendered.
  return (
    <ComboboxList ref={scrollRef}>
      {renderItems.map((item) => {
        if (isCreatable(item)) {
          return (
            <ComboboxItem
              key={`__create__${item.__create}`}
              value={item}
              className="[contain-intrinsic-size:auto_2rem] [content-visibility:auto]"
              title={
                renderCreateOption
                  ? undefined
                  : t(($) => $.comboboxCreate, {
                      query: item.__create,
                    })
              }
            >
              {renderCreateOption ? (
                renderCreateOption(item.__create)
              ) : (
                <>
                  <PlusIcon />
                  <span className="truncate">
                    {t(($) => $.comboboxCreate, {
                      query: item.__create,
                    })}
                  </span>
                </>
              )}
            </ComboboxItem>
          );
        }
        const label = getLabel(item);
        const description = getDescription?.(item);
        return (
          <ComboboxItem
            key={getKey(item)}
            value={item}
            title={label}
            className={cn(
              '[content-visibility:auto]',
              description ? '[contain-intrinsic-size:auto_2.75rem]' : '[contain-intrinsic-size:auto_2rem]'
            )}
          >
            {description ? (
              <div className="flex min-w-0 flex-col">
                <div className="truncate">{label}</div>
                <div className="truncate text-muted-foreground text-xs">{description}</div>
              </div>
            ) : (
              label
            )}
          </ComboboxItem>
        );
      })}
      {hasMore && <div ref={tailRef} aria-hidden="true" className="h-px" />}
      {/* Only while appending to rows already on screen. With no rows the popup is `data-empty`, so `ComboboxEmpty`
          shows its own loading copy and a row here would render a second one. */}
      {isLoading && renderItems.length > 0 && (
        <output aria-live="polite" className="block px-2 py-2 text-center text-muted-foreground text-sm">
          {t(($) => $.comboboxLoading)}
        </output>
      )}
    </ComboboxList>
  );
};
